import { useEffect, useRef, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import {
  GOV_PRIORITIES,
  GovItemDetail,
  GovPriority,
  createItem,
  governanceErrorMessage,
} from "../../../../utils/icGovernance";
import { priorityLabel } from "../governanceStatus";
import { CreateDialogShell, optional } from "./dialogParts";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (item: GovItemDetail) => void;
};

const NewChangeRequestDialog = ({ open, onClose, onCreated }: Props) => {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [detailedRequest, setDetailedRequest] = useState("");
  const [businessReason, setBusinessReason] = useState("");
  const [priority, setPriority] = useState<GovPriority>("MEDIUM");
  const [neededBy, setNeededBy] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards a double click that lands before the disabled button re-renders.
  const savingRef = useRef(false);

  // A fresh form every time the dialog opens.
  useEffect(() => {
    if (open) {
      setTitle("");
      setSummary("");
      setDetailedRequest("");
      setBusinessReason("");
      setPriority("MEDIUM");
      setNeededBy("");
      setError(null);
      setSaving(false);
      savingRef.current = false;
    }
  }, [open]);

  const canSubmit = title.trim().length > 0 && summary.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const item = await createItem({
        kind: "CHANGE_REQUEST",
        title: title.trim(),
        description: summary.trim(),
        detailedRequest: optional(detailedRequest),
        businessReason: optional(businessReason),
        priority,
        dueDate: /^\d{4}-\d{2}-\d{2}$/.test(neededBy) ? neededBy : undefined,
      });
      onCreated(item);
    } catch (err) {
      setError(governanceErrorMessage(err));
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <CreateDialogShell
      open={open}
      titleId="new-change-request-title"
      title="New change request"
      saving={saving}
      error={error}
      canSubmit={canSubmit}
      submitLabel="Submit change request"
      onClose={onClose}
      onSubmit={submit}
    >
      <TextField
        label="Change title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
        autoFocus
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Summary"
        placeholder="What should change, in a sentence or two"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        required
        multiline
        minRows={3}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Detailed request"
        value={detailedRequest}
        onChange={(e) => setDetailedRequest(e.target.value)}
        multiline
        minRows={3}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Business reason"
        value={businessReason}
        onChange={(e) => setBusinessReason(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        disabled={saving}
      />
      <TextField
        select
        label="Priority"
        value={priority}
        onChange={(e) => setPriority(e.target.value as GovPriority)}
        fullWidth
        disabled={saving}
      >
        {GOV_PRIORITIES.map((p) => (
          <MenuItem key={p} value={p}>
            {priorityLabel(p)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Needed by"
        type="date"
        value={neededBy}
        onChange={(e) => setNeededBy(e.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth
        disabled={saving}
      />
    </CreateDialogShell>
  );
};

export default NewChangeRequestDialog;
